import React, { useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { PlayerStatus, S3_URL } from '../../config';
import useViewport from '../../hooks/useViewport';
import { convertPhcpToString } from '../../utils/convertHcpToString';
import { getTranslation } from '../../utils/getTranslation';
import HiddenHole from '../icons/HiddenHole';
import LoadingSpinner from './LoadingSpinner';
import ScoreTableRounds from './ScoreTableRounds';
import { isIndividualTeamFormatId, isTeamScoreTeamFormatId, showStablefordPoints, someScoresEnteredForPlayerRound, } from '../../utils/gameFormatUtils';
import { getFormattedTime } from '../../utils/date';
import classNames from 'classnames';
import { EditIcon } from '../icons/Edit';
import { CheckmarkIcon } from '../icons/CheckMark';
var SortResetHoverOptions;
(function (SortResetHoverOptions) {
    SortResetHoverOptions["POSITION"] = "pos";
    SortResetHoverOptions["SCORE"] = "score";
})(SortResetHoverOptions || (SortResetHoverOptions = {}));
export default function ScoreTable(_a) {
    var game = _a.game, leaderboard = _a.leaderboard, showCountryFlags = _a.showCountryFlags, isLoading = _a.isLoading, isScratch = _a.isScratch, _b = _a.enableToggleScoreSheets, enableToggleScoreSheets = _b === void 0 ? true : _b, _c = _a.units, units = _c === void 0 ? 'metric' : _c, editCallback = _a.editCallback, isWeekly = _a.isWeekly, timezone = _a.timezone, _d = _a.verifyScores, verifyScores = _d === void 0 ? false : _d;
    var intl = useIntl();
    var _e = useState([]), roundsOpenFor = _e[0], setRoundsOpenFor = _e[1];
    var _f = useState(null), sortedByRound = _f[0], setSortedByRound = _f[1];
    var _g = useState(null), sortableHover = _g[0], setSortableHover = _g[1];
    var _h = useState(null), sortResetHover = _h[0], setSortResetHover = _h[1];
    var sortByStartTime = function (a, b) {
        var roundIndex = sortedByRound ? sortedByRound - 1 : 0;
        var startTimeA = a.rounds[roundIndex].startTime;
        var startTimeB = b.rounds[roundIndex].startTime;
        if (startTimeA && startTimeB) {
            return new Date(startTimeA).getTime() - new Date(startTimeB).getTime();
        }
        return 0;
    };
    var players = useMemo(function () {
        if (!game) {
            return [];
        }
        var playerList = (game === null || game === void 0 ? void 0 : game.players) || [];
        var showPoints = showStablefordPoints(game.id);
        var nonSortableStatuses = [
            PlayerStatus.DQ.toLowerCase(),
            PlayerStatus.DNS.toLowerCase(),
            PlayerStatus.RTD.toLowerCase(),
        ];
        var nonSortablePlayers = playerList
            .slice()
            .filter(function (p) { return (p.status ? nonSortableStatuses.includes(p.status) : false); });
        var sortablePlayers = playerList.slice().filter(function (p) { return !nonSortablePlayers.includes(p); });
        if (!sortedByRound) {
            // Maybe sort by startTime
            var playersWithoutPosition_1 = sortablePlayers
                .slice()
                .filter(function (p) { return p.position === '-' && p.status !== PlayerStatus.CUT.toLowerCase(); });
            if (playersWithoutPosition_1.length > 0) {
                // CUT players are not sorted but appended after players with startTime
                var cutPlayers_1 = sortablePlayers.slice().filter(function (p) { return p.status === PlayerStatus.CUT.toLowerCase(); });
                // Players with position are already sorted
                var playersWithPosition = sortablePlayers
                    .slice()
                    .filter(function (p) { return !playersWithoutPosition_1.includes(p) && !cutPlayers_1.includes(p); });
                // Players without position are sorted by startTime
                playersWithoutPosition_1.sort(function (a, b) { return sortByStartTime(a, b); });
                return playersWithPosition.concat(playersWithoutPosition_1, cutPlayers_1, nonSortablePlayers);
            }
            // No sorting required
            return playerList;
        }
        // Sort players by a specific round score
        var sortedPlayers = sortablePlayers.slice().sort(function (a, b) {
            var _a, _b;
            var first = showPoints ? b : a;
            var second = showPoints ? a : b;
            return Number((_a = first.rounds[sortedByRound - 1]) === null || _a === void 0 ? void 0 : _a.scoreTotal) - Number((_b = second.rounds[sortedByRound - 1]) === null || _b === void 0 ? void 0 : _b.scoreTotal);
        });
        return sortedPlayers.concat(nonSortablePlayers);
    }, [game, sortedByRound]); // eslint-disable-line react-hooks/exhaustive-deps
    var roundCount = useMemo(function () { return (leaderboard === null || leaderboard === void 0 ? void 0 : leaderboard.rounds.length) || 0; }, [leaderboard]);
    var isMultiRound = useMemo(function () { return roundCount > 1; }, [roundCount]);
    var isTeamGame = useMemo(function () { var _a; return ((_a = players[0]) === null || _a === void 0 ? void 0 : _a.teamId) !== undefined; }, [players]);
    var isIndiTeamGameFormat = useMemo(function () { return (game ? isIndividualTeamFormatId(game === null || game === void 0 ? void 0 : game.id) : false); }, [game]);
    var viewport = useViewport();
    var isMobile = viewport.isMobile, isTablet = viewport.isTablet, isSmallDesktop = viewport.isSmallDesktop, isDesktop = viewport.isDesktop;
    var countryFlagsVisible = showCountryFlags && !isTeamGame;
    var openRounds = function (player) {
        if (!playerHasScore(player)) {
            return;
        }
        var selected = isTeamGame ? parseInt(player.teamId) : player.userId;
        setRoundsOpenFor(function (prev) { return (!prev.includes(selected) ? prev.concat(selected) : prev.filter(function (p) { return p !== selected; })); });
    };
    var getFlag = function (player) {
        if (!player.countryCode) {
            return '';
        }
        var countryCode = player.countryCode, country = player.country;
        return React.createElement("img", { src: "".concat(S3_URL, "/flags/").concat(countryCode, ".png"), alt: country, className: "score-table-flag" });
    };
    var isActiveRow = function (player) {
        var selected = isTeamGame ? parseInt(player.teamId) : player.userId;
        return roundsOpenFor.includes(selected);
    };
    var playerHasScore = function (player) {
        return player.position !== '-' || player.thruToday !== '-';
    };
    var getToggleClass = function (player) {
        return isActiveRow(player) ? 'open' : 'closed';
    };
    var showPlayingHcp = function () {
        var _a, _b;
        if (isMultiRound) {
            return false;
        }
        if (isTeamGame && game) {
            return isTeamScoreTeamFormatId(game.id);
        }
        var firstPlayerPhcp = ((_b = (_a = game === null || game === void 0 ? void 0 : game.players[0]) === null || _a === void 0 ? void 0 : _a.rounds[0]) === null || _b === void 0 ? void 0 : _b.playingHcp) || 0;
        return firstPlayerPhcp !== 0;
    };
    var renderPlayerNameOrTeamWithHcp = function (player) {
        if (player.team) {
            return (React.createElement(React.Fragment, null,
                React.createElement("span", { className: "team-name" },
                    player.name,
                    " ",
                    showPlayingHcp() && getPlayingHcp(player)),
                React.createElement("span", { className: "team-players" }, player.team.map(function (p) { return p.name.split(' ').pop(); }).join(', '))));
        }
        return showPlayingHcp() ? player.name + getPlayingHcp(player) : player.name;
    };
    var getPlayingHcp = function (player) {
        var _a, _b;
        var playingHcp = ((_a = player.rounds[0]) === null || _a === void 0 ? void 0 : _a.playingHcp) ? convertPhcpToString((_b = player.rounds[0]) === null || _b === void 0 ? void 0 : _b.playingHcp) : null;
        return playingHcp ? " (".concat(playingHcp, ")") : '';
    };
    var getRoundNumbers = function () {
        return Array.from({ length: roundCount }, function (_, i) { return i + 1; });
    };
    var isNumeric = function (value) {
        return !isNaN(parseInt(value));
    };
    var isUnderPar = function (player) {
        return isNumeric(player.toPar) && parseInt(player.toPar) < 0;
    };
    var hasHiddenHoles = function () {
        var rounds = players.map(function (p) { return p.rounds; }).flat();
        if (isIndiTeamGameFormat && !isScratch) {
            var teamPlayers = players.map(function (p) { return p.team; }).flat();
            rounds = teamPlayers.map(function (p) { return p.rounds; }).flat();
        }
        var hiddenFlags = rounds.map(function (r) { return r.hidden; }).flat();
        return hiddenFlags.some(function (h) { return h; }) === true;
    };
    var getRoundTotal = function (round) {
        var hasHiddenHoles = round.hidden.some(function (h) { return h; });
        var allStrokesNotVisible = round.strokes.some(function (s) { return s === ''; });
        return hasHiddenHoles && allStrokesNotVisible ? (React.createElement(HiddenHole, null)) : (React.createElement("div", { style: { display: 'flex', justifyContent: 'center' } },
            round.scoreTotal,
            isMultiRound && round.verified && verifyScores && (React.createElement("sup", null,
                React.createElement(CheckmarkIcon, { width: 'sm', height: 'sm' })))));
    };
    var thruTodayOrStartTime = function (player) {
        // Players with a status are not starting or are finished
        if (!isMultiRound && !player.status) {
            var round = player.rounds[0];
            var leaderboardRound = leaderboard.rounds[0];
            var playerHasScores = someScoresEnteredForPlayerRound({ player: player, gameFormatId: game.id });
            if (leaderboardRound.isConfigured && !playerHasScores && round.startTime) {
                return getFormattedTime(round.startTime, units, timezone);
            }
        }
        return player.thruToday;
    };
    var multiroundGetRoundTotalOrStartTime = function (player, roundIndex) {
        var round = player.rounds[roundIndex];
        // If player has a status, don't show start time
        if (player.status) {
            return getRoundTotal(round);
        }
        var leaderboardRound = leaderboard.rounds[roundIndex];
        var playerHasScores = someScoresEnteredForPlayerRound({ player: player, gameFormatId: game.id, roundIndex: roundIndex });
        if (roundCount > 1 && leaderboardRound.isConfigured && !playerHasScores && round.startTime) {
            return React.createElement(React.Fragment, null, getFormattedTime(round.startTime, units, timezone));
        }
        return getRoundTotal(round);
    };
    var getTotal = function (player) {
        var roundsHiddenData = player.rounds.map(function (r) { return r.hidden; }).flat();
        var roundsStrokeData = player.rounds.map(function (r) { return r.strokes; }).flat();
        var hasHiddenHoles = roundsHiddenData.some(function (h) { return h; });
        var allStrokesNotVisible = roundsStrokeData.some(function (s) { return s === ''; });
        return hasHiddenHoles && allStrokesNotVisible ? React.createElement(HiddenHole, null) : React.createElement(React.Fragment, null, player.score);
    };
    var getPlayedHoles = function (player) {
        var _a, _b, _c;
        var playedRounds = (_a = player.rounds[0]) === null || _a === void 0 ? void 0 : _a.played.filter(function (p) { return p; }).length;
        if (isIndiTeamGameFormat && !isScratch) {
            playedRounds = (_c = (_b = player.team[0]) === null || _b === void 0 ? void 0 : _b.rounds[0]) === null || _c === void 0 ? void 0 : _c.played.filter(function (p) { return p; }).length;
        }
        return playedRounds > 0 ? playedRounds : '-';
    };
    var isDesktopResolution = function () {
        return isDesktop || isSmallDesktop;
    };
    var renderActiveRowRounds = function (player) {
        var _a;
        var usePlayerRounds = isTeamGame && ((_a = player.team[0]) === null || _a === void 0 ? void 0 : _a.rounds) !== undefined;
        if (isTeamGame && usePlayerRounds) {
            return (React.createElement(React.Fragment, null, player.team.map(function (p, i) { return (React.createElement(ScoreTableRounds, { key: i, leaderboard: leaderboard, player: p, roundNumbers: getRoundNumbers(), isTeamGame: isTeamGame, isMultiRound: isMultiRound, viewport: viewport, gameFormatId: game === null || game === void 0 ? void 0 : game.id, isScratch: isScratch, timezone: timezone })); })));
        }
        return (React.createElement(ScoreTableRounds, { leaderboard: leaderboard, player: player, roundNumbers: getRoundNumbers(), isTeamGame: isTeamGame, isMultiRound: isMultiRound, viewport: viewport, gameFormatId: game === null || game === void 0 ? void 0 : game.id, isScratch: isScratch, isWeekly: isWeekly, units: units, timezone: timezone }));
    };
    var isFirstToMissCut = function (player, index) {
        var _a;
        if (sortedByRound !== null) {
            return false;
        }
        return player.position === PlayerStatus.CUT && ((_a = players[index - 1]) === null || _a === void 0 ? void 0 : _a.position) !== PlayerStatus.CUT;
    };
    var renderRoundTitle = function (roundNumber) {
        var roundNumberText = getTranslation(intl, 'roundShort', { round: roundNumber });
        if ([sortedByRound, sortableHover].includes(roundNumber)) {
            return (React.createElement("div", { className: "sortable-container" },
                roundNumberText,
                " ",
                React.createElement("div", { className: "sortable" })));
        }
        return React.createElement(React.Fragment, null, roundNumberText);
    };
    var maybeRenderSortableHover = function (title, resetOption) {
        if (sortResetHover === resetOption && sortedByRound !== null) {
            return (React.createElement("div", { className: "sortable-container" },
                title,
                " ",
                React.createElement("div", { className: "sortable" })));
        }
        return React.createElement(React.Fragment, null, title);
    };
    var onClickLeaderboardRow = function (p) {
        if (enableToggleScoreSheets) {
            if (!isActiveRow(p)) {
                // Opening scorecard, scroll this row into view
                setTimeout(function () {
                    var _a;
                    var offsetPadding = 80;
                    var activeRow = document.getElementById("activeRow-".concat(p.userId || p.teamId));
                    // Less than 768 wide, we can't scroll the .overflow-auto div since it's full height at that point.
                    // We need to scroll the whole page instead
                    if ((isMobile || isTablet) && activeRow) {
                        var windowOffset = activeRow.getBoundingClientRect().top + window.scrollY;
                        !!windowOffset && window.scrollTo({ top: windowOffset - offsetPadding, behavior: 'smooth' });
                        return;
                    }
                    if (activeRow) {
                        var offSet = activeRow.classList.contains('leader') ? offsetPadding : activeRow === null || activeRow === void 0 ? void 0 : activeRow.offsetTop;
                        !!offSet &&
                            ((_a = document
                                .querySelector('#game-leaderboard-container .overflow-auto')) === null || _a === void 0 ? void 0 : _a.scrollTo({ top: offSet - offsetPadding, behavior: 'smooth' }));
                    }
                }, 100);
            }
            openRounds(p);
        }
    };
    if (isLoading && !players.length) {
        return React.createElement(LoadingSpinner, null);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("table", { className: "score-table" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", { className: "w40 cursor", onClick: function () { return setSortedByRound(null); }, onMouseEnter: function () { return setSortResetHover(SortResetHoverOptions.POSITION); }, onMouseLeave: function () { return setSortResetHover(null); } }, isMobile === false &&
                        maybeRenderSortableHover(getTranslation(intl, 'pos'), SortResetHoverOptions.POSITION)),
                    countryFlagsVisible && isDesktopResolution() && React.createElement("th", { className: "w40" }, getTranslation(intl, 'country')),
                    React.createElement("th", null,
                        getTranslation(intl, 'name'),
                        " ",
                        showPlayingHcp() && React.createElement("span", null,
                            "(",
                            getTranslation(intl, 'playingHcp'),
                            ")")),
                    enableToggleScoreSheets && React.createElement("th", { className: "w40" }),
                    React.createElement("th", { className: "w60 nowrap" }, getTranslation(intl, 'toPar')),
                    React.createElement("th", { className: "w60" }, getTranslation(intl, 'thru')),
                    hasHiddenHoles() && React.createElement("th", { className: "w60" }, getTranslation(intl, 'played')),
                    isMultiRound && (React.createElement(React.Fragment, null,
                        React.createElement("th", { className: "w60" }, getTranslation(intl, 'today')),
                        isDesktop && React.createElement("th", { className: "w20" }),
                        isDesktop &&
                            getRoundNumbers().map(function (n, i) { return (React.createElement("th", { key: i, className: "w60 cursor", onClick: function () { return setSortedByRound(n); }, onMouseEnter: function () { return setSortableHover(n); }, onMouseLeave: function () { return setSortableHover(null); } }, renderRoundTitle(n))); }),
                        isDesktop && React.createElement("th", { className: "w20" }))),
                    React.createElement("th", { className: "w60 cursor", onClick: function () { return setSortedByRound(null); }, onMouseEnter: function () { return setSortResetHover(SortResetHoverOptions.SCORE); }, onMouseLeave: function () { return setSortResetHover(null); } }, maybeRenderSortableHover(getTranslation(intl, 'score'), SortResetHoverOptions.SCORE)),
                    !isMultiRound && verifyScores && React.createElement("th", { className: "w60" }, getTranslation(intl, 'verified')),
                    editCallback && React.createElement("th", { className: "w20" }))),
            React.createElement("tbody", null, players.map(function (p, i) {
                var _a;
                return (React.createElement(React.Fragment, { key: i },
                    isFirstToMissCut(p, i) && (React.createElement("tr", { className: "cut-line" },
                        React.createElement("td", { colSpan: 9999 }, getTranslation(intl, 'missedCut')))),
                    React.createElement("tr", { className: classNames('player', isActiveRow(p) && 'active', enableToggleScoreSheets && 'cursor', i === 0 && 'leader'), id: isActiveRow(p) ? "activeRow-".concat(p.userId || p.teamId) : undefined, onClick: function () { return onClickLeaderboardRow(p); } },
                        React.createElement("td", { className: "bold medium w40 mobile-center" }, p.position),
                        countryFlagsVisible && isDesktopResolution() && React.createElement("td", { className: "w40" }, getFlag(p)),
                        React.createElement("td", { className: "bold player-name" }, renderPlayerNameOrTeamWithHcp(p)),
                        enableToggleScoreSheets && (React.createElement("td", { className: "score-table-toggle w-40" }, playerHasScore(p) && React.createElement("div", { className: getToggleClass(p) }))),
                        React.createElement("td", { className: isUnderPar(p) ? 'bold large under-par w60' : 'bold large w60' }, p.toPar),
                        React.createElement("td", { className: "w60 medium nowrap" }, thruTodayOrStartTime(p)),
                        hasHiddenHoles() && React.createElement("td", { className: "w60 medium" }, getPlayedHoles(p)),
                        isMultiRound && (React.createElement(React.Fragment, null,
                            React.createElement("td", { className: "w60 medium nowrap" }, p.toParToday),
                            isDesktop && React.createElement("td", { className: "w20" }),
                            isDesktop &&
                                getRoundNumbers().map(function (n, i) { return (React.createElement("td", { key: i, className: "w60 medium" }, multiroundGetRoundTotalOrStartTime(p, n - 1))); }),
                            isDesktop && React.createElement("td", { className: "w20" }))),
                        React.createElement("td", { className: "bold large w60" }, getTotal(p)),
                        !isMultiRound && verifyScores && (React.createElement("td", { className: "w60" }, ((_a = p.rounds[0]) === null || _a === void 0 ? void 0 : _a.verified) && React.createElement(CheckmarkIcon, null))),
                        editCallback && (React.createElement("td", { className: "w20 cursor" },
                            React.createElement(EditIcon, { onClick: function (e) {
                                    e.stopPropagation();
                                    editCallback(p);
                                } })))),
                    enableToggleScoreSheets && isActiveRow(p) && renderActiveRowRounds(p)));
            })),
            React.createElement("tfoot", null)),
        isLoading && React.createElement("div", { className: "loading-shimmer" })));
}
